import type {CreateApiKeyDataADto, CreateApiKeyResponseADto} from '@cohort/admin-schemas/apiKeys';
import {CreateApiKeyDataASchema} from '@cohort/admin-schemas/apiKeys';
import Button from '@cohort/merchants/components/buttons/Button';
import {InputWithLabel} from '@cohort/merchants/components/form/InputWithLabel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@cohort/merchants/components/modals/Dialog';
import {merchantsKeys} from '@cohort/merchants/hooks/api/Merchants';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {createApiKey} from '@cohort/merchants/lib/api/ApiKeys';
import {zodResolver} from '@hookform/resolvers/zod';
import {Check} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import {Fragment, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

interface CreateApiKeyModalProps {
  handleOnClose: () => void;
}

interface CreateAPIKeyFormProps {
  onSuccess: (key: CreateApiKeyResponseADto) => void;
  onCancel: () => void;
}

interface ShowCreatedKeyProps {
  apiKey: CreateApiKeyResponseADto;
  onClose: () => void;
}

const CreateAPIKeyForm: React.FC<CreateAPIKeyFormProps> = ({onSuccess, onCancel}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const queryClient = useQueryClient();
  const {t} = useTranslation('pages', {keyPrefix: 'settings.integrations.createApiKeyModal'});

  const {register, handleSubmit, formState} = useForm<CreateApiKeyDataADto>({
    resolver: zodResolver(CreateApiKeyDataASchema),
    mode: 'onTouched',
    defaultValues: {
      name: t('formInputNamePlaceholder'),
    },
  });

  const {isLoading, mutate: createApiKeyMutation} = useCohortMutation({
    mutationFn: async ({data}: {data: CreateApiKeyDataADto}) => createApiKey(merchantId, data),
    onSuccess: key => {
      void queryClient.invalidateQueries(merchantsKeys.settings(merchantId));
      onSuccess(key);
    },
  });

  return (
    <Fragment>
      <div className="flex flex-col justify-center text-center">
        <DialogTitle className="text-lg font-bold leading-6">{t('formTitle')}</DialogTitle>
      </div>
      <form
        onSubmit={handleSubmit(data => createApiKeyMutation({data}))}
        data-testid="create-campaign-modal"
      >
        <div className="mb-6 mt-4">
          <InputWithLabel
            label={t('formInputNameLabel')}
            type="text"
            autoFocus
            onFocus={e => e.target.select()}
            {...register('name')}
            error={formState.errors.name?.message}
          />
        </div>
        <div className="flex items-center justify-end gap-4">
          <Button
            variant="secondary"
            disabled={!formState.isValid || isLoading}
            onClick={() => onCancel()}
          >
            {t('cancelBtn')}
          </Button>
          <Button type="submit" disabled={!formState.isValid || isLoading} loading={isLoading}>
            {t('createBtn')}
          </Button>
        </div>
      </form>
    </Fragment>
  );
};

const ShowCreatedKey: React.FC<ShowCreatedKeyProps> = ({apiKey, onClose}) => {
  const {t} = useTranslation('pages', {keyPrefix: 'settings.integrations.createApiKeyModal'});
  const [copied, setCopied] = useState(false);

  return (
    <Fragment>
      <DialogTitle className="flex flex-col items-center gap-4 text-center text-lg font-bold leading-6">
        <div className="rounded-full bg-green-100 p-2 text-green-600">
          <Check className="h-8 w-8" />
        </div>
        {t('createdKeyTitle')}
      </DialogTitle>
      <DialogDescription className="mt-2 text-center text-sm">
        {t('createdKeySubtitle')}
      </DialogDescription>
      <div className="my-8 flex w-full flex-row items-center gap-4 break-all rounded-md border-2 border-dashed p-4">
        <p className="text-sm">{apiKey.plaintextKey}</p>
        <Button
          variant="secondary"
          onClick={async () => {
            await navigator.clipboard.writeText(apiKey.plaintextKey);
            setCopied(true);
          }}
        >
          {copied ? (
            <Fragment>
              <Check className="mr-2 h-4 w-4 text-green-600" />
              {t('createdKeyCopyBtnCopied')}
            </Fragment>
          ) : (
            t('createdKeyCopyBtn')
          )}
        </Button>
      </div>
      <Button className="w-full" onClick={() => onClose()}>
        {t('createdKeyBtn')}
      </Button>
    </Fragment>
  );
};

const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({handleOnClose}) => {
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponseADto>();

  return (
    <Dialog open onOpenChange={handleOnClose}>
      <DialogContent>
        <div className="p-6 [width:500px]">
          {createdKey === undefined && (
            <CreateAPIKeyForm onCancel={handleOnClose} onSuccess={key => setCreatedKey(key)} />
          )}
          {createdKey && <ShowCreatedKey apiKey={createdKey} onClose={handleOnClose} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateApiKeyModal;
